/* eslint-disable react/prop-types */
import * as React from 'react';

import { Button, notification } from 'antd';

import { CopyOutlinedIcon } from 'components';
import { useCopyToClipboard } from 'hooks';
import { cn, CONFIG, PATHNAME } from 'utils';

const {
  MEDIA: { NESHAN_URL: MEDIA_NESHAN_URL },
} = CONFIG;
const {
  QUERY_PARAM: {
    DEFAULT: { COMMA: DEFAULT_COMMA },
  },
} = PATHNAME;

const PrimitiveIframe = React.forwardRef<
  React.ElementRef<'iframe'>,
  React.ComponentPropsWithoutRef<'iframe'>
>(
  (
    {
      title = 'iframe',
      loading = 'lazy',
      referrerPolicy = 'no-referrer-when-downgrade',
      allowFullScreen = true,
      className,
      ...props
    },
    ref,
  ) => (
    <iframe
      ref={ref}
      title={title}
      loading={loading}
      referrerPolicy={referrerPolicy}
      allowFullScreen={allowFullScreen}
      className={cn('size-full border-none', className)}
      {...props}
    />
  ),
);
PrimitiveIframe.displayName = 'PrimitiveIframe';

type PrimitiveNeshanIframeProps = StrictOmit<
  React.ComponentPropsWithoutRef<typeof PrimitiveIframe>,
  'src'
> &
  Record<'lat' | 'long', string>;

const PrimitiveNeshanIframe = React.forwardRef<
  React.ElementRef<typeof PrimitiveIframe>,
  PrimitiveNeshanIframeProps
>(({ lat, long, title = 'نقشه نشان', ...props }, ref) => {
  const { copyToClipboard } = useCopyToClipboard();

  const neshanSrc = React.useMemo(
    () => `${MEDIA_NESHAN_URL}/maps/iframe/places/${lat}/${long}`,
    [lat, long],
  );
  const coordinates = React.useMemo(
    () => `${lat}${DEFAULT_COMMA}${long}`,
    [lat, long],
  );

  const handleCopyCoordinates = React.useCallback(async () => {
    try {
      const success = await copyToClipboard(coordinates);

      if (success) {
        notification.success({
          message: 'مختصات با موفقیت کپی شد.',
        });
      } else {
        notification.info({
          message: 'کپی کردن پشتیبانی نمی شود.',
        });
      }
    } catch (error) {
      notification.error({
        message: error instanceof Error ? error.message : 'خطای ناشناخته',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coordinates]);

  return (
    <div className='relative size-full overflow-auto'>
      <PrimitiveIframe ref={ref} title={title} src={neshanSrc} {...props} />
      <div className='absolute left-1 top-1'>
        <Button
          size='small'
          className='h-auto p-1.5 text-sm'
          aria-label='کپی مختصات'
          icon={<CopyOutlinedIcon />}
          onClick={handleCopyCoordinates}
        >
          کپی مختصات
        </Button>
      </div>
    </div>
  );
});
PrimitiveNeshanIframe.displayName = 'PrimitiveNeshanIframe';

export { PrimitiveIframe, PrimitiveNeshanIframe };
